import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { CITYAREA_TYPE } from '../../constants/api';
import {
  createCityAreaInitialize,
  deleteCityAreaInitialize,
  updateCityAreaInitialize,
} from '../actions/api';
import { cityAreaByIdSelector } from '../apiFeatures/cityAreaSlice';
import { parseApiError } from '../globalMessageSlice';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const updateCityArea = createAction(
  'page/administrationCityArea/changeCityAreaIsActive',
);
const setSelectedCityAreaId = createAction(
  'page/administrationCityArea/setSelectedCityAreaId',
);
const deleteCityArea = createAction(
  'page/administrationCityArea/deleteCityArea',
);
const setCityAreaBeingUpdated = createAction(
  'page/administrationCityArea/cityAreaBeingUpdated',
);
const setIsPanelOpen = createAction(
  'page/administrationCityArea/setIsPanelOpen',
);
const createCityArea = createAction(
  'page/administrationCityArea/createCityArea',
);
const setCityAreaBeingCreated = createAction(
  'page/administrationCityArea/cityAreaBeingCreated',
);

// ---------- REDUCERS -----------
const cityAreaAdministrationPageReducer = createReducer(
  {
    selectedCityAreaId: null,
    isPanelOpen: false,
    isUpdatingCityArea: false,
    isCreatingCityArea: false,
  },
  (builder) => {
    builder
      .addCase(setSelectedCityAreaId, (state, action) => {
        state.selectedCityAreaId = action.payload;
      })
      .addCase(setCityAreaBeingUpdated, (state, action) => {
        state.isUpdatingCityArea = action.payload;
      })
      .addCase(setIsPanelOpen, (state, action) => {
        state.isPanelOpen = action.payload;
      })
      .addCase(setCityAreaBeingCreated, (state, action) => {
        state.isCreatingCityArea = action.payload;
      });
  },
);
// ----------- Sagas -------------
export function* cityAreaAdministrationPageSaga() {
  yield all([
    fork(changeCityAreaIsActiveSaga),
    fork(deleteCityAreaSaga),
    fork(createCityAreaSaga),
  ]);
}

function* changeCityAreaIsActiveSaga() {
  yield takeLatest(updateCityArea.type, function* (action) {
    yield put(setCityAreaBeingUpdated(true));
    const { cityAreaId, isActive } = action.payload;

    const cityArea = yield select(cityAreaByIdSelector(cityAreaId));

    const newCityArea = Object.assign({}, cityArea, {
      attributes: { ...cityArea.attributes, isActive: isActive },
      relationships: { ...cityArea.relationships },
    });

    const updateCityAreaInitAction = yield put(
      updateCityAreaInitialize({
        cityAreaId,
        changes: newCityArea,
      }),
    );

    const updateCityAreaResult = yield awaitApiCall(updateCityAreaInitAction);

    if (updateCityAreaResult.error) {
      yield put(parseApiError(updateCityAreaResult.error.payload));
    }
    yield put(setCityAreaBeingUpdated(false));
  });
}

function* deleteCityAreaSaga() {
  yield takeLatest(deleteCityArea.type, function* (action) {
    yield put(setCityAreaBeingUpdated(true));

    const cityAreaId = action.payload;

    const deleteCityAreaInitAction = yield put(
      deleteCityAreaInitialize(cityAreaId),
    );

    const deleteCityAreaResult = yield awaitApiCall(deleteCityAreaInitAction);

    if (deleteCityAreaResult.success) {
      yield put(setSelectedCityAreaId(null));
    } else if (deleteCityAreaResult.error) {
      yield put(parseApiError(deleteCityAreaResult.error.payload));
    }

    yield put(setCityAreaBeingUpdated(false));
  });
}

function* createCityAreaSaga() {
  yield takeLatest(createCityArea.type, function* (action) {
    yield put(setCityAreaBeingCreated(true));
    const title = action.payload;

    const newCityAreaModel = {
      type: CITYAREA_TYPE,
      attributes: {
        name: title,
        isActive: true,
      },
      relationships: {},
    };

    const createCityAreaInitAction = yield put(
      createCityAreaInitialize({ data: newCityAreaModel }),
    );

    const createCityAreaResult = yield awaitApiCall(createCityAreaInitAction);

    if (createCityAreaResult.error) {
      yield put(parseApiError(createCityAreaResult.error.payload));
    }
    yield put(setIsPanelOpen(false));
    yield put(setCityAreaBeingCreated(false));
  });
}
// ----------- Selectors -----------

const cityAreaAdministrationState = (state) =>
  state.page.cityAreaAdministration;

const isUpdatingCityAreaSelector = createSelector(
  cityAreaAdministrationState,
  (state) => state.isUpdatingCityArea,
);

const isPanelOpenSelector = createSelector(
  cityAreaAdministrationState,
  (state) => state.isPanelOpen,
);

const selectedCityAreaIdSelector = createSelector(
  cityAreaAdministrationState,
  (state) => state.selectedCityAreaId,
);

const isCreatingCityAreaSelector = createSelector(
  cityAreaAdministrationState,
  (state) => state.isCreatingCityArea,
);

export {
  updateCityArea,
  setSelectedCityAreaId,
  selectedCityAreaIdSelector,
  deleteCityArea,
  isUpdatingCityAreaSelector,
  isPanelOpenSelector,
  setIsPanelOpen,
  createCityArea,
  isCreatingCityAreaSelector,
};

export default cityAreaAdministrationPageReducer;
